import { Dispatch, SetStateAction, createContext } from 'react';

import { BatchLTLReplySummary } from 'types/BatchLTLReply';
import { Maybe } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';

export enum DrumkitPlatform {
  Outlook = 'Outlook',
  Front = 'Front',
  Sidepanel = 'Sidepanel',
  None = 'None',
}

export function isEmailPlatform(platform: DrumkitPlatform): boolean {
  return (
    platform === DrumkitPlatform.Outlook || platform === DrumkitPlatform.Front
  );
}

export function supportsDraftResponse(platform: DrumkitPlatform): boolean {
  return (
    platform === DrumkitPlatform.Outlook ||
    platform === DrumkitPlatform.Front ||
    platform === DrumkitPlatform.Sidepanel
  );
}

export type SidebarState = {
  serviceName: string;
  inboxEmailAddress: string;
  subject: string; // For debugging
  // Outlook threading fields
  threadId: string;
  emailId: number;
  threadItemId: string;
  // Gmail threading fields (RFC standard)
  threadReferences: string;
  inReplyTo: string[];
  isOutlookReply: boolean;
  isSharedMailboxEmail: boolean;
  isChromeSidePanel: boolean;
  tabId: number;
  drumkitPlatform: DrumkitPlatform;
  // Thread participant information for email replies
  threadFrom: string;
  threadTo: string[];
  threadCc: string[];
  drumkitAppContainer: Maybe<Element>;
  starredLoads: string[];
  viewedLoads: string[];
  clickedSuggestion: Maybe<GenericSuggestion>;
  shouldKitAutoSelect: boolean;
  /**
   * Quick Quote suggestion ids currently selected for Batch Quote.
   * Used to keep Kit pills in sync with lanes in the Batch form.
   */
  batchSelectedSuggestionIds: number[];
  /**
   * When true, show the Kit "Scroll to top" pill (set on terminating action while
   * pending suggestions remain; cleared by the pill on dismiss).
   */
  showKitScrollToTop: boolean;
  // Last Batch LTL reply on this thread; reset with the rest of the state on thread change.
  batchLTLReplySummary: Maybe<BatchLTLReplySummary>;
  /**
   * Suggestions available for the current view (Load or Quote).
   * Kit marks completed suggestions Accepted (keeps them in the list) rather than removing them.
   */
  curSuggestionList: GenericSuggestion[];
  /** Freight tracking IDs on the current email thread (for titlebar Loads pulse). */
  threadFreightTrackingIDs: string[];
  /** Pending carrier / appointment / check-call suggestions from Load view (thread + load). */
  hasThreadLoadRelatedSuggestions: boolean;
  /**
   * Quick Quote, Load Building, truck list, or carrier-quote preload activity for Quote view.
   * `null` until QuoteSidebar or Loads prefetch has resolved it for the current thread.
   */
  hasQuoteViewFeatureSuggestions: Maybe<boolean>;
  /**
   * Most recent Quick Quote request id for the current email thread (ENG-7582).
   * Load build uses it to map Quote Number without requiring a Builder AI suggestion.
   * Cleared when the email thread changes (provider resets to defaultSidebarState).
   */
  latestQuoteRequestId: number;
  /**
   * Select a Kit suggestion (sets `clickedSuggestion`). Prefer `suggestionID`;
   * otherwise the first pending suggestion in `suggestionPipeline`.
   */
  goToSuggestion: (params: {
    suggestionID?: number;
    suggestionPipeline?: SuggestionPipelines;
  }) => Promise<void>;
  // Allow parent repos to override the default function for opening external URLs (e.g. SDKs)
  openExternalUrl: (url: string, target?: string, features?: string) => void;

  // TODO: add load status here so we can auto refresh it after an info update
  // TODO: add operator list here so we don't need to fetch it every time we switch to load info tab
};

export const defaultSidebarState = {
  serviceName: '',
  inboxEmailAddress: '',
  subject: '',
  threadId: '',
  emailId: 0,
  threadItemId: '',
  threadReferences: '',
  inReplyTo: [],
  isOutlookReply: false,
  isSharedMailboxEmail: false,
  isChromeSidePanel: false,
  tabId: 0,
  drumkitPlatform: DrumkitPlatform.None,
  threadFrom: '',
  threadTo: [],
  threadCc: [],
  drumkitAppContainer: null,
  starredLoads: [],
  viewedLoads: [],
  clickedSuggestion: null,
  shouldKitAutoSelect: true,
  batchSelectedSuggestionIds: [],
  showKitScrollToTop: false,
  batchLTLReplySummary: null,
  curSuggestionList: [],
  threadFreightTrackingIDs: [],
  hasThreadLoadRelatedSuggestions: false,
  hasQuoteViewFeatureSuggestions: null,
  latestQuoteRequestId: 0,
  goToSuggestion: async () => {},
  openExternalUrl: (url: string, target?: string, features?: string) =>
    window.open(url, target ?? '_blank', features ?? 'noopener,noreferrer'),
};

type SidebarStateContextType = {
  currentState: SidebarState;
  setCurrentState: Dispatch<SetStateAction<SidebarState>>;
};

export const SidebarStateContext = createContext<SidebarStateContextType>({
  currentState: defaultSidebarState,
  setCurrentState: async () => {},
});
